import { PreviewCard } from "@base-ui/react/preview-card"
import PropTypes from "prop-types"
import React, { createContext, useContext } from "react"

import BottomSheet from "src/components/BottomSheet"

import useIsTouchDevice from "src/hooks/use_is_touch_device"

const ARROW_CLASSES = [
  "flex",
  "data-[side=bottom]:-top-2 data-[side=bottom]:rotate-0",
  "data-[side=left]:right-[-13px] data-[side=left]:rotate-90",
  "data-[side=right]:left-[-13px] data-[side=right]:-rotate-90",
  "data-[side=top]:-bottom-2 data-[side=top]:rotate-180",
].join(" ")

const ArrowSvg = () => (
  <svg width="20" height="10" viewBox="0 0 20 10" fill="none">
    <path
      d="M9.66437 2.60207L4.80758 6.97318C4.07308 7.63423 3.11989 8 2.13172 8H0V10H20V8H18.5349C17.5468 8 16.5936 7.63423 15.8591 6.97318L11.0023 2.60207C10.622 2.2598 10.0447 2.25979 9.66437 2.60207Z"
      className="fill-white"
    />
    <path
      d="M10.3333 3.34539L5.47654 7.71648C4.55842 8.54279 3.36693 9 2.13172 9H0V8H2.13172C3.11989 8 4.07308 7.63423 4.80758 6.97318L9.66437 2.60207C10.0447 2.25979 10.622 2.2598 11.0023 2.60207L15.8591 6.97318C16.5936 7.63423 17.5468 8 18.5349 8H20V9H18.5349C17.2998 9 16.1083 8.54278 15.1901 7.71648L10.3333 3.34539Z"
      className="fill-gray-200"
    />
  </svg>
)

const PreviewSheetContext = createContext({
  href: "",
  cta: null,
  isDesktop: true,
})

const PreviewSheetRoot = ({
  children,
  href,
  cta,
  delay = 0,
  closeDelay = 0,
}) => {
  const isTouchDevice = useIsTouchDevice()
  const isDesktop = !isTouchDevice

  const contextValue = { href, cta, isDesktop, delay, closeDelay }

  if (isDesktop) {
    return (
      <PreviewSheetContext.Provider value={contextValue}>
        <PreviewCard.Root>{children}</PreviewCard.Root>
      </PreviewSheetContext.Provider>
    )
  }

  return (
    <PreviewSheetContext.Provider value={contextValue}>
      <BottomSheet.Root>{children}</BottomSheet.Root>
    </PreviewSheetContext.Provider>
  )
}

PreviewSheetRoot.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string.isRequired,
  cta: PropTypes.node,
  delay: PropTypes.number,
  closeDelay: PropTypes.number,
}

const Trigger = ({ children }) => {
  const { href, isDesktop, delay, closeDelay } = useContext(PreviewSheetContext)

  if (isDesktop) {
    return (
      <PreviewCard.Trigger
        delay={delay}
        closeDelay={closeDelay}
        href={href}
        render={
          href ? (
            <a />
          ) : (
            <span className="flex" style={{ width: "fit-content" }} />
          )
        }
      >
        {children}
      </PreviewCard.Trigger>
    )
  }

  return (
    <BottomSheet.Trigger>
      <button
        type="button"
        className="inline-block whitespace-nowrap bg-transparent px-0 font-semibold text-blue-700 hover:underline focus:outline-none focus:ring print:hidden"
      >
        {children}
      </button>
    </BottomSheet.Trigger>
  )
}

Trigger.propTypes = {
  children: PropTypes.node.isRequired,
}

const Content = ({ children }) => {
  const { href, cta, isDesktop } = useContext(PreviewSheetContext)

  if (isDesktop) {
    return (
      <PreviewCard.Portal>
        <PreviewCard.Positioner side="bottom" sideOffset={4}>
          <PreviewCard.Popup className="z-50 rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
            {children}
            <PreviewCard.Arrow className={ARROW_CLASSES}>
              <ArrowSvg />
            </PreviewCard.Arrow>
          </PreviewCard.Popup>
        </PreviewCard.Positioner>
      </PreviewCard.Portal>
    )
  }

  return (
    <BottomSheet.Content>
      <BottomSheet.Handle />
      {children}
      {cta && href && (
        <div className="mt-4 border-t border-gray-200 pt-4">
          <a href={href} className="block">
            {cta}
          </a>
        </div>
      )}
    </BottomSheet.Content>
  )
}

Content.propTypes = {
  children: PropTypes.node.isRequired,
}

const PreviewSheet = PreviewSheetRoot
PreviewSheet.Trigger = Trigger
PreviewSheet.Content = Content

export default PreviewSheet
